import { tr, ro } from 'date-fns/locale'
import _ from 'lodash'
import moment from 'moment'

import { eventEmitter } from '@/event-bus.js'
import i18n from '@/i18n/i18n.js'
import { HOLIDAY_TYPE } from '@/constants/holidayType.js'
import country from './country.js'

const { t } = i18n.global

import 'numeral/locales/tr'

export function build_query(
  query,
  allowedFilters,
  allowedIncludes,
  allowedSorts = null,
  allowedAppends = null,
  allowedFields = null
) {
  const newQuery = {}

  if (query) {
    _.forEach(query, (v, k) => {
      if (allowedFilters.indexOf(k) != -1) {
        newQuery['filter[' + k + ']'] = v
      } else {
        newQuery[k] = v
      }
    })
  }

  if (allowedIncludes) newQuery['include'] = allowedIncludes

  if (query && query.sort) {
    newQuery['sort'] = query.sort
  } else {
    if (allowedSorts) newQuery['sort'] = allowedSorts
  }

  if (allowedAppends) newQuery['append'] = allowedAppends

  if (allowedFields) {
    _.forEach(allowedFields, (v, k) => {
      if (v.includes(':')) {
        const parts = v.split(':')
        newQuery['fields[' + parts[0] + ']'] = parts[1]
      } else {
        newQuery['fields[]'] = v
      }
    })
  }

  return newQuery
}

export function build_query_with_prefix(query, allowedFilters, prefix) {
  const newQuery = {}

  if (query) {
    _.forEach(query, (v, k) => {
      if (allowedFilters.indexOf(k) !== -1) {
        newQuery['filter[' + prefix + '.' + k + ']'] = v
      } else {
        newQuery[k] = v
      }
    })
  }

  return newQuery
}

export function selectTextToCopy(element) {
  let range
  if (document.selection) {
    // IE
    range = document.body.createTextRange()
    range.moveToElementText(element)
    range.select()
  } else if (window.getSelection) {
    range = document.createRange()
    range.selectNode(element)
    window.getSelection().removeAllRanges()
    window.getSelection().addRange(range)
  }
}

export function errorMessage(err) {
  if (!err || !err?.response) {
    return t('constants.error_messages.application_error_messages.timeout')
  } else if (err.response.data.errors) {
    const errors = err.response.data.errors
    const firstKey = _.get(_.keys(errors), 0)
    return errors[firstKey][0]
  } else if (err.response.data.message) {
    if (displayHTMLContent(err.response.data.message).type === 'html') {
      return displayHTMLContent(err.response.data.message).content
    } else {
      return err.response.data.message
    }
  }
  return ''
}

/**
 * Did the backend refuse this request because the retailer's sales channel is closed?
 *
 * Every such refusal is a 422 with an errors key that contains IsSalesChannelActiveValidationGuard:
 * `<machine>.guard.IsSalesChannelActiveValidationGuard.fail` for a machine step, and the bare
 * guard name for a create request.
 *
 * @param {Object} err the rejected request
 * @returns {boolean}
 */
export function isSalesChannelClosedError(err) {
  const response = err?.response

  if (response?.status !== 422) {
    return false
  }

  return Object.keys(response.data?.errors ?? {}).some(key => key.includes('IsSalesChannelActiveValidationGuard'))
}

export function stringToHslColor(str, s, l) {
  var hash = 0
  var i
  for (i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  var h = hash % 360
  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)'
}

export function validNameValidator(value) {
  const words = value.split(/\s/)
  let uppercase = false
  let isSuccess = true

  words.forEach(word => {
    uppercase = word.charAt(1) === word.charAt(1).toUpperCase()

    for (let i = 0; i < word.length; i++) {
      if (i === 0) {
        if (word.charAt(i) !== word.charAt(i).toUpperCase()) {
          isSuccess = false
          return false
        }
      } else {
        if (uppercase) {
          if (word.charAt(i) !== word.charAt(i).toUpperCase()) {
            isSuccess = false
            return false
          }
        } else {
          if (word.charAt(i) !== word.charAt(i).toLowerCase()) {
            isSuccess = false
            return false
          }
        }
      }
    }
  })

  return isSuccess
}

export const characterControl = text => {
  if (!text) {
    return true
  }
  const rule = /[`!@#$^*=[\]{}'"\\|./?~]/
  return !rule.test(text)
}

export const getCurrentLanguage = () => {
  switch (i18n.global.locale.value) {
    case 'tr':
      return tr.code
    case 'ro':
      return ro.code
    default:
      return ''
  }
}

export const getDatePickerMarkers = (type = '') => {
  if (!type) {
    return [
      {
        date: new Date(),
        type: 'line',
        color: '#6cb87a'
      }
    ]
  }
}

export const getTimeRange = (startDate, endDate, type, checkEquals = false) => {
  const fromDate = moment(startDate)
  const toDate = moment(endDate)
  let diff = toDate.diff(fromDate, type)

  if (!checkEquals && diff > 0) {
    diff--
  }

  const range = []
  for (let i = 1; i <= diff; i++) {
    range.push(moment(startDate).add(i, type).toDate())
  }
  return range
}

export const getFormatterForDatePicker = (date, yearShortFix = false) => {
  const day = date.getDate()
  let month = date.getMonth() + 1
  let year = date.getFullYear()

  if (yearShortFix === true) {
    year = year.toString().substring(2)
    if (!month.toString().startsWith('1') || (month.toString().startsWith('1') && month.toString().length === 1)) {
      month = '0' + month.toString()
    }
  }

  return `${day}.${month}.${year}`
}

export const isInteger = value => {
  return (value / 100) % 1
}

export const roundNumberWithPrecision = (number, param) => {
  return Number.parseFloat(Number.parseFloat(number).toFixed(param))
}

export const isMobileDeviceAndroid = () => {
  return /(android)/i.test(navigator.userAgent)
}

export const isAnyMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export const isDeviceTablet = () => {
  return /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(navigator.userAgent)
}

export const maskPhoneNumber = phone => {
  phone = phone.toString().replaceAll(' ', '').replace('(', '').replace(')', '')

  if (country.is('tr') && phone.length === 10) {
    return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)} ${phone.substring(6, 8)} ${phone.substring(8)}`
  } else if (country.is('ro') && phone.length === 9) {
    return `(${phone.substring(0, 3)}) ${phone.substring(3, 6)} ${phone.substring(6)}`
  }

  return phone
}

export const sma256Hash = async string => {
  const utf8 = new TextEncoder().encode(string)
  const hashBuffer = await crypto.subtle.digest('SHA-256', utf8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(bytes => bytes.toString(16).padStart(2, '0')).join('')
  return hashHex
}

export const addError = (eventName, message) =>
  eventEmitter.emit('addError', {
    type: eventName,
    message: message
  })

export const removeError = eventName => eventEmitter.emit('removeError', eventName)

export const getDocumentContentType = path => {
  const loweredPath = path.toLowerCase()
  const extension = loweredPath.match(/\.([a-zA-Z0-9]+)$/)[1]

  const mimeTypes = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    png: 'image/png'
  }

  return mimeTypes[extension] || 'application/pdf'
}

export const validateNumberInput = event => {
  const charCode = event.charCode

  if (!(charCode >= 48 && charCode <= 57)) {
    return event.preventDefault()
  }
}

export const isFloat = value => {
  if (typeof value === 'number' && !Number.isNaN(value) && !Number.isInteger(value)) {
    return true
  }

  return false
}

export const maskBeginingToEndWithStars = (prefixStringLength, suffixStringLength, stringValue) => {
  const prefixString = stringValue.substring(0, prefixStringLength)
  const suffixString = stringValue.slice(-suffixStringLength)
  const stringWithStarts = stringValue.length - (prefixStringLength + suffixStringLength)

  return prefixString + '*'.repeat(stringWithStarts) + suffixString
}

export const capitalizeFirstLetterOfWords = text => {
  const words = text.split(' ')

  const capitalize = words.map(word => word.charAt(0).toUpperCase() + word.slice(1))

  return capitalize.join(' ')
}

export function optional(value, defaultValue = null) {
  if (value === undefined || value === null || value === '') {
    return typeof defaultValue === 'function' ? (defaultValue.call({}) ?? null) : defaultValue
  }

  return value
}

export function getValidationErrorMessage(error, field) {
  const errors = error?.response?.data?.errors

  if (errors === null) {
    return null
  }

  field = errors[field]

  if (field === undefined) {
    return null
  }

  return field[0] ?? null
}

export const displayHTMLContent = message => {
  const dangerousTagsAndAttributes =
    /<script.*?>.*?<\/script>|<iframe.*?>.*?<\/iframe>|<style.*?>.*?<\/style>|on\w+="[^"]*"|on\w+='[^']*'/gi

  if (_.isEmpty(message)) {
    return { type: 'text', content: '' }
  }

  const safeContent = message.replace(dangerousTagsAndAttributes, '')

  return /<[a-z][\s\S]*>/i.test(safeContent)
    ? { type: 'html', content: safeContent }
    : { type: 'text', content: safeContent }
}

export const getBrowserName = () => {
  const userAgent = navigator.userAgent
  if (userAgent.indexOf('Edg') > -1) {
    return 'Microsoft Edge'
  } else if (userAgent.indexOf('Chrome') > -1) {
    return 'Chrome'
  } else if (userAgent.indexOf('Firefox') > -1) {
    return 'Firefox'
  } else if (userAgent.indexOf('Safari') > -1) {
    return 'Safari'
  } else if (userAgent.indexOf('Opera') > -1) {
    return 'Opera'
  } else if (userAgent.indexOf('Trident') > -1 || userAgent.indexOf('MSIE') > -1) {
    return 'Internet Explorer'
  }

  return t('shared.common.browser')
}

export function isCreditCardSingleName(value) {
  if (!value) return true

  const words = value.split(/\s+/).filter(word => word.length > 0)

  if (words.length < 2) return false

  return true
}

export function creditCardValidNameValidator(value) {
  if (!value) return false

  if (/\d/.test(value)) return false

  const words = value.split(/\s+/).filter(word => word.length > 0)

  const regex = /^[A-Za-zÇçĞğİıÖöŞşÜü]+$/

  // Check each word for invalid characters
  for (const word of words) {
    if (!regex.test(word)) {
      return false
    }
  }

  return true
}

/**
 * Formats a date range for display in a single input field
 * @param {Array} dateRange - Array containing start and end dates [startDate, endDate]
 * @returns {String} Formatted date range string (e.g., "23.03.25 - 24.05.25")
 */
export const formatDateRange = dateRange => {
  if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
    return ''
  }

  const [startDate, endDate] = dateRange

  if (!startDate || !endDate) {
    return ''
  }

  const formattedStartDate = getFormatterForDatePicker(startDate, true)
  const formattedEndDate = getFormatterForDatePicker(endDate, true)

  return `${formattedStartDate} - ${formattedEndDate}`
}

export const isHoliday = (holidays, date) => {
  // Returns true on weekends
  if ([6, 7].includes(moment(date).isoWeekday())) {
    return true
  }

  return holidays.some(holiday => {
    return moment(date).isBetween(moment(holiday.start_date).startOf('day'), moment(holiday.end_date).endOf('day'), null, '[]')
  })
}

export const nextRetailerPaymentDate = (paymentDays, holidays, date) => {
  const retailerHolidays = holidays.filter(holiday =>
    [HOLIDAY_TYPE.PUBLIC_HOLIDAY, HOLIDAY_TYPE.NON_RETAILER_PAYMENT_DAY].includes(holiday.type)
  )

  let currentDate = moment(date)
  while (paymentDays.includes(currentDate.isoWeekday()) === false || isHoliday(retailerHolidays, currentDate) === true) {
    currentDate = currentDate.add(1, 'day')
  }

  return currentDate
}
